<template>
    <brick-settings-popup
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_GoalSettings_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_GoalSettings_Delete_Body'),
            brickTitle: title
        }"
        :brick="brick"
        :name.sync="newName"
        :invalid="$v.$invalid || isLoading || !customConditionsValid"

        @save="onSave()"
        @delete="$emit('delete')"
        @hidden="$emit('hidden')"
        @display-errors="displayErrorMessages()"
    >
        <settings-item icon="fas fa-plane-arrival" :label="resource('JourneyBuilder_GoalSettings_GoalType_Label')">
            <template #input="{id}">
                <v-select class='form-control'
                          v-model="goalTypeModel"

                          :input-id="id"
                          :options="selectableGoalTypes"
                          :disabled="!canWrite || selectableGoalTypes.length === 0"
                          :clearable="false"

                          searchable
                          filterable
                          taggable

                          close-on-select
                >
                    <div slot='no-options' />
                </v-select>
            </template>
            <template #errors>
                <required-error-message v-if="$v.goalTypeModel.$dirty && $v.goalTypeModel.$invalid" :field-name="resource('JourneyBuilder_GoalSettings_GoalType_Label')" />
            </template>
        </settings-item>
        
        <settings-item :label="resource('JourneyBuilder_GoalSettings_DeleteRecords_Label')">
            <template #input>
                <toggle-button 
                    v-model="settings.deleteRecords"
                    
                    :sync="true"
                    :labels="{checked: resource('Bool_Yes'), unchecked: resource('Bool_No')}"
                    :width="78"
                />
            </template>
        </settings-item>
        
        <goal-custom-conditions
            ref="customConditions"
            
            v-model="settings.customConditions"
            
            :goal-type="goalTypeModel && goalTypeModel.id"

            @loading-state-changed="isLoading = $event"
            @validation-state-changed="customConditionsValid = $event"
        />
    </brick-settings-popup>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import { brickDefinitions } from "@modules/JourneyBuilder/Bricks/brick-definitions";
import { required } from 'vuelidate/lib/validators'
import { bootstrapDangerGrowl } from "@core/Util/growler";
import sprintf from 'sprintf';

import resourceMixin from '@core/Util/Mixins/resources';
import brickSettingsMixin from '../Mixins/brick-settings'
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import { validationMixin } from 'vuelidate';

import BrickSettingsPopup from '../BrickSettingsPopup';
import GoalCustomConditions from './GoalCustomConditions';
import InputGroup from '@core/Components/Inputs/InputGroup';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import VSelect from 'vue-select';
import { ToggleButton } from 'vue-js-toggle-button';

export default {
    name: "GoalBrickSettings",
    components: {
        RequiredErrorMessage,
        BrickSettingsPopup,
        GoalCustomConditions,
        InputGroup,
        VSelect,
        ToggleButton
    },
    mixins: [resourceMixin, brickSettingsMixin, permissionsMixin, validationMixin],
    
    validations: {
        goalTypeModel: {
            required
        }
    },
    
    data(){
        return {
            goalTypes: [],
            touchpointTypes: [],

            isLoading: true,
            customConditionsValid: true
        }
    },
    
    computed: {
        /**
         * https://datanext.atlassian.net/browse/DPN-2845
         * Generate a default name for the brick based on its configuration
         *
         * @return {String} default name
         */
        defaultName(){
            if(this.goalTypeModel){
                return this.goalTypeModel.label;
            }

            return brickDefinitions[this.brickType].displayName;
        },

        /**
         * @returns {JourneyBrick[]}
         */
        sourceBricks(){
            return this.brick.incomingConnections.map(connection => connection.sourceBrick);
        },
        
        sourceBrickTypes() {
            return this.sourceBricks.reduce((types, brick) => {
                if(brick.config && brick.config.touchpointParentType) {
                    types.push(brick.config.touchpointParentType);
                }
                
                return types;
            }, []);
        },
        
        title(){
            if(this.goalTypeModel){
                return sprintf(this.resource('JourneyBuilder_GoalSettings_Title'), this.goalTypeModel.label);
            }
            
            return '';
        },
        
        selectableGoalTypes() {
            return this.goalTypes.filter(gT => gT.touchPointParentType === null || this.sourceBrickTypes.includes(gT.touchPointParentType));
        },
        
        goalTypeModel:{
            get() {
                return this.selectableGoalTypes.find(gT => gT.id === this.settings.goalType);
            },
            set(value){
                this.$v.goalTypeModel.$touch();
                this.settings.goalType = value && value.id;
            }
        }
    },
    
    watch: {
        defaultName(newDefaultName, oldDefaultName){
            //Update our name to the new default when the user hasn't entered a name themselves
            if(this.newName === oldDefaultName){
                this.newName = newDefaultName;
            }
        }
    },
    
    async created() {
        this.settings = {
            //Preserve values set from external locations
            ...this.settings,

            //Explicitly declare values which we'll be modifying
            goalType: this.brick.config.goalType,
            customConditions: this.brick.config.customConditions || [],
            deleteRecords: this.brick.config.deleteRecords || false,
            goal: true //Potentially used in backend by DSS
        } 
        
        try {
            this.goalTypes = await api.getGoalTypes();
            this.touchpointTypes = await api.getTouchpointTypes();   
        } catch (e) {
           bootstrapDangerGrowl(e); 
        }
    },

    methods: {
        onSave(){
            this.saveSettings();
        },
        
        displayErrorMessages(){
            this.$v.$touch();
            
            if(this.$refs.customConditions) {
                this.$refs.customConditions.triggerValidations();
            }
        }
    }
}
</script>
